/* eslint-disable */
import { useState } from "react";
import { C, fmt$, fmtPct, responsiveFontSize } from "../shared/globals.js";

// ── helpers ───────────────────────────────────────────────────────────────────
const fmtPrice = v => {
  if (!v && v !== 0) return "$—";
  if (v === 0) return "$0";
  if (v < 0.000001) return "$" + v.toFixed(10);
  if (v < 0.00001)  return "$" + v.toFixed(8);
  if (v < 0.0001)   return "$" + v.toFixed(7);
  if (v < 0.001)    return "$" + v.toFixed(6);
  if (v < 0.01)     return "$" + v.toFixed(5);
  if (v < 0.1)      return "$" + v.toFixed(4);
  if (v < 1)        return "$" + v.toFixed(3);
  if (v < 10)       return "$" + v.toFixed(3);
  if (v < 1000)     return "$" + v.toFixed(2);
  return new Intl.NumberFormat("en-US", { style:"currency", currency:"USD", minimumFractionDigits:2 }).format(v);
};

// Calculate a nice step for input/slider based on magnitude
const niceStep = v => {
  if (!v || v === 0) return 0.000001;
  const mag = Math.pow(10, Math.floor(Math.log10(v)) - 2);
  return Math.max(mag, 1e-10);
};

// ── TargetPrice ───────────────────────────────────────────────────────────────
export function TargetPrice({ totalShares, totalDollars, currentPrice }) {
  const cp  = currentPrice || 0;
  const MAX = cp > 0 ? cp * 20 : 10000;
  const step = niceStep(cp);

  const [target, setTarget] = useState(() => cp > 0 ? cp : 1);

  const tp     = parseFloat(target) || 0;
  const safeTP = Math.max(0, tp);

  const targetPortfolioValue = totalShares * safeTP;
  const targetPnl  = targetPortfolioValue - totalDollars;
  const targetPct  = totalDollars > 0 ? (targetPnl / totalDollars) * 100 : 0;
  const distToTarget = cp > 0 ? ((safeTP - cp) / cp) * 100 : null;
  const sliderPct  = MAX > 0 ? Math.min(100, Math.max(0, (safeTP / MAX) * 100)) : 0;

  // Quick multiplier buttons
  const multipliers = [0.5, 1, 2, 5, 10];

  return (
    <div>
      {/* Input */}
      <div style={{ marginBottom:16 }}>
        <div style={{ display:"inline-flex", alignItems:"center", gap:6,
          background:"var(--bg4)", border:`1px solid ${C.orange}`, borderRadius:8, padding:"8px 12px" }}>
          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:16, fontWeight:700, color:C.orange }}>$</span>
          <input
            type="number"
            min={0} max={MAX} step={step}
            value={target}
            onChange={e => setTarget(e.target.value)}
            style={{ background:"transparent", border:"none", outline:"none",
              color:"var(--text)", fontFamily:"Montserrat,sans-serif",
              fontSize:20, fontWeight:900, width:120, colorScheme:"dark" }}
          />
        </div>
        {cp > 0 && distToTarget !== null && (
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, marginTop:6,
            color: safeTP >= cp ? C.green : C.red }}>
            {safeTP >= cp ? "▲" : "▼"} {Math.abs(distToTarget).toFixed(2)}% {safeTP >= cp ? "above" : "below"} current ({fmtPrice(cp)})
          </div>
        )}
      </div>

      {/* Slider */}
      <div style={{ position:"relative", marginBottom:20 }}>
        <div style={{ position:"relative", height:6, background:"var(--border)", borderRadius:3, cursor:"pointer" }}>
          <div style={{ position:"absolute", left:0, width:`${sliderPct}%`, height:"100%", background:C.orange, borderRadius:3 }}/>
          <div style={{
            position:"absolute", left:`${sliderPct}%`, top:"50%",
            transform:"translate(-50%, -50%)",
            width:14, height:14, borderRadius:"50%",
            background:C.orange, border:"2px solid var(--bg2)",
            boxShadow:`0 0 0 2px ${C.orange}55`,
            pointerEvents:"none", zIndex:1,
          }}/>
          {/* Current price marker */}
          {cp > 0 && (
            <div style={{
              position:"absolute",
              left:`${Math.min(100,(cp/MAX)*100)}%`,
              top:-6, transform:"translateX(-50%)",
              width:2, height:18, background:"#a78bfa", borderRadius:1,
            }}>
              <div style={{ position:"absolute", top:20, left:"50%", transform:"translateX(-50%)",
                fontFamily:"Montserrat,sans-serif", fontSize:7, fontWeight:700,
                color:"#a78bfa", whiteSpace:"nowrap" }}>NOW</div>
            </div>
          )}
          <input
            type="range" min={0} max={MAX} step={step}
            value={safeTP}
            onChange={e => setTarget(e.target.value)}
            style={{ position:"absolute", top:"50%", transform:"translateY(-50%)",
              left:0, width:"100%", cursor:"pointer", zIndex:10, background:"transparent" }}
          />
        </div>
        {/* Quick multiplier buttons */}
        <div style={{ display:"flex", justifyContent:"space-between", marginTop:10 }}>
          {multipliers.map(m => {
            const val = cp * m;
            const isActive = Math.abs(safeTP - val) < step * 2;
            return (
              <span key={m} onClick={() => setTarget(String(val))}
                style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700,
                  color: isActive ? C.orange : "var(--text5)",
                  cursor:"pointer", transition:"color 0.15s" }}>
                {m}x
              </span>
            );
          })}
        </div>
      </div>

      {/* Result cards */}
      <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
        <div style={{ flex:1, minWidth:130, background:"var(--bg4)", borderRadius:8, padding:"12px 16px" }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", marginBottom:6 }}>PORTFOLIO AT TARGET</div>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:responsiveFontSize(fmt$(targetPortfolioValue)), fontWeight:700, color:"var(--text)" }}>
            {fmt$(targetPortfolioValue)}
          </div>
        </div>
        <div style={{ flex:1, minWidth:130, background:"var(--bg4)", borderRadius:8, padding:"12px 16px" }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", marginBottom:6 }}>P&L AT TARGET</div>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:responsiveFontSize(fmt$(targetPnl)), fontWeight:700,
            color: targetPnl >= 0 ? C.green : C.red }}>
            {fmt$(targetPnl)}
          </div>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700,
            color: targetPct >= 0 ? C.green : C.red, marginTop:3 }}>
            {fmtPct(targetPct)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── AvgCalcCompact ────────────────────────────────────────────────────────────
export function AvgCalcCompact({ currentPrice, totalShares, totalDollars, avgPrice }) {
  const [amount, setAmount] = useState(500);
  const MIN = 0, MAX = 20000;

  const addAmount = parseFloat(amount) || 0;
  const cp = currentPrice || 0;
  const newShares  = addAmount > 0 && cp > 0 ? addAmount / cp : 0;
  const newAvg     = (totalShares + newShares) > 0 ? (totalDollars + addAmount) / (totalShares + newShares) : 0;
  const avgDrop    = avgPrice > 0 ? newAvg - avgPrice : 0;
  const avgDropPct = avgPrice > 0 ? ((newAvg - avgPrice) / avgPrice) * 100 : 0;
  const hasResult  = addAmount > 0;
  const pct = MAX > 0 ? Math.min(100, Math.max(0, (addAmount / MAX) * 100)) : 0;

  const fmtShares = v => {
    if (v === 0) return "0";
    if (v < 0.0001) return v.toFixed(8);
    if (v < 0.01)   return v.toFixed(6);
    if (v < 1)      return v.toFixed(4);
    return v.toFixed(4);
  };

  return (
    <div>
      {/* Input */}
      <div style={{ marginBottom:16 }}>
        <div style={{ display:"inline-flex", alignItems:"center", gap:6,
          background:"var(--bg4)", border:`1px solid ${C.orange}`, borderRadius:8, padding:"8px 12px" }}>
          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:16, fontWeight:700, color:C.orange }}>$</span>
          <input
            type="number" step="1" placeholder="0" min={MIN} max={MAX}
            value={amount}
            onChange={e => setAmount(e.target.value)}
            style={{ background:"transparent", border:"none", outline:"none",
              color:"var(--text)", fontFamily:"Montserrat,sans-serif",
              fontSize:20, fontWeight:900, width:90, colorScheme:"dark" }}
          />
        </div>
        {hasResult && (
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, marginTop:6,
            color: avgDrop <= 0 ? C.green : C.red }}>
            {avgDrop <= 0 ? "▼" : "▲"} {fmt$(Math.abs(avgDrop))} ({(avgDropPct >= 0 ? "+" : "") + avgDropPct.toFixed(2) + "%"})
          </div>
        )}
      </div>

      {/* Slider */}
      <div style={{ position:"relative", marginBottom:24 }}>
        <div style={{ position:"relative", height:6, background:"var(--border)", borderRadius:3, cursor:"pointer" }}>
          <div style={{ position:"absolute", left:0, width:`${pct}%`, height:"100%", background:C.orange, borderRadius:3 }}/>
          <div style={{
            position:"absolute", left:`${pct}%`, top:"50%",
            transform:"translate(-50%, -50%)",
            width:14, height:14, borderRadius:"50%",
            background:C.orange, border:"2px solid var(--bg2)",
            boxShadow:`0 0 0 2px ${C.orange}55`,
            pointerEvents:"none", zIndex:1,
          }}/>
          <input
            type="range" min={MIN} max={MAX} step={100}
            value={parseFloat(amount) || 0}
            onChange={e => setAmount(e.target.value)}
            style={{ position:"absolute", top:"50%", transform:"translateY(-50%)",
              left:0, width:"100%", cursor:"pointer", zIndex:10, background:"transparent" }}
          />
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", marginTop:8 }}>
          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text5)" }}>$0</span>
          {[5000, 10000, 15000].map(v => (
            <span key={v} onClick={() => setAmount(String(v))}
              style={{ fontFamily:"Montserrat,sans-serif", fontSize:8,
                color: parseFloat(amount) === v ? C.orange : "var(--text5)",
                cursor:"pointer", transition:"color 0.15s" }}>
              ${(v/1000).toFixed(0)}k
            </span>
          ))}
          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text5)" }}>$20k</span>
        </div>
      </div>

      {/* Result cards */}
      <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
        <div style={{ flex:1, minWidth:100, background:"var(--bg4)", borderRadius:8, padding:"12px 16px" }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", marginBottom:6 }}>SHARES BOUGHT</div>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:responsiveFontSize(hasResult ? `+${fmtShares(newShares)}` : "—"), fontWeight:700,
            color: hasResult ? C.green : "var(--text5)" }}>
            {hasResult ? `+${fmtShares(newShares)}` : "—"}
          </div>
        </div>
        <div style={{ flex:1, minWidth:100, background:"var(--bg4)", borderRadius:8, padding:"12px 16px" }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", marginBottom:6 }}>OLD AVG</div>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:responsiveFontSize(fmtPrice(avgPrice)), fontWeight:700,
            color:"var(--text3)", textDecoration: hasResult ? "line-through" : "none", opacity: hasResult ? 0.5 : 1 }}>
            {fmtPrice(avgPrice)}
          </div>
        </div>
        <div style={{ flex:1, minWidth:100, background:"var(--bg4)", borderRadius:8, padding:"12px 16px",
          border: hasResult ? `1px solid ${avgDrop <= 0 ? C.green : C.red}` : "none" }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", marginBottom:6 }}>NEW AVG</div>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:responsiveFontSize(hasResult ? fmtPrice(newAvg) : "—"), fontWeight:700,
            color: hasResult ? (avgDrop <= 0 ? C.green : C.red) : "var(--text5)" }}>
            {hasResult ? fmtPrice(newAvg) : "—"}
          </div>
          {hasResult && (
            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700,
              color: avgDrop <= 0 ? C.green : C.red, marginTop:3 }}>
              {(avgDropPct >= 0 ? "+" : "") + avgDropPct.toFixed(2) + "%"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
